import React, { useState, useEffect } from "react";
import SimpleReactValidator from "simple-react-validator";

// Import your icons
import userIcon from "../../images/icon/user-balck-icon.svg";
import emailIcon from "../../images/icon/sms-balck-icon.svg";
import phoneIcon from "../../images/icon/call-icon02.svg";
import uploadIcon from "../../images/icon/upload-icon.svg";
import listIcon from "../../images/icon/list-icon.svg";
import messageIcon from "../../images/icon/messages-icon.svg";
import arrowIcon from "../../images/icon/rotate-arrow-black02.svg";
import moneyIcon from "../../images/icon/money-cash.svg";

const ContactForm: React.FC = () => {
  const [forms, setForms] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    message: "",
    file: null as File | null,
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  const [validator] = useState(
    new SimpleReactValidator({ className: "errorMessage" }),
  );

  // Auto-close popup logic
  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => setStatus(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const changeHandler = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setForms({ ...forms, [name]: value });
    if (validator.allValid()) validator.hideMessages();
  };

  const fileHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setForms({ ...forms, file });
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validator.allValid()) {
      setLoading(true);
      const GOOGLE_SHEET_URL =
        "https://script.google.com/macros/s/AKfycbxo2zd_A9s4LaYJY-FFdAVhlyONQ2s_WgbIrM3vSO10e7PjeacXD2v2aaX0i55_Kt5G/exec";

      let fileData = null;
      let fileName = null;
      let fileType = null;

      if (forms.file) {
        fileData = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = (reader.result as string).split(",")[1];
            resolve(base64);
          };
          reader.readAsDataURL(forms.file!);
        });
        fileName = forms.file.name;
        fileType = forms.file.type;
      }

      try {
        await fetch(GOOGLE_SHEET_URL, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...forms, fileData, fileName, fileType }),
        });

        setStatus({ type: "success", msg: "Form submitted successfully!" });
        setForms({
          name: "",
          email: "",
          phone: "",
          service: "",
          budget: "",
          message: "",
          file: null,
        });
        validator.hideMessages();
      } catch (error) {
        setStatus({
          type: "error",
          msg: "Something went wrong. Try again!",
        });
      } finally {
        setLoading(false);
      }
    } else {
      validator.showMessages();
      setForms({ ...forms });
    }
  };

  return (
    <>
      {/* Bulletproof Popup */}
      {status && (
        <div className={`custom-status-popup ${status.type}`}>{status.msg}</div>
      )}

      <form onSubmit={submitHandler} className="xb-contact-input-form">
        <div className="row mt-none-20">
          {/* Name */}
          <div className="col-lg-6 col-md-6 mt-20">
            <div className="xb-input-field">
              <input
                name="name"
                type="text"
                value={forms.name}
                onChange={changeHandler}
                placeholder="Your Name*"
                required
              />
              <img src={userIcon} alt="user" />
            </div>
            {validator.message("name", forms.name, "required|alpha_space")}
          </div>

          {/* Email */}
          <div className="col-lg-6 col-md-6 mt-20">
            <div className="xb-input-field">
              <input
                name="email"
                type="email"
                value={forms.email}
                onChange={changeHandler}
                placeholder="Email Address*"
                required
              />
              <img src={emailIcon} alt="email" />
            </div>
            {validator.message("email", forms.email, "required|email")}
          </div>

          {/* Phone */}
          <div className="col-lg-6 col-md-6 mt-20">
            <div className="xb-input-field">
              <input
                name="phone"
                type="text"
                value={forms.phone}
                onChange={changeHandler}
                placeholder="Contact No*"
                required
              />
              <img src={phoneIcon} alt="phone" />
            </div>
            {validator.message("phone", forms.phone, "required|numeric")}
          </div>

          {/* File */}
          <div className="col-lg-6 col-md-6 mt-20">
            <div className="xb-input-field xb-select-file">
              <input type="file" onChange={fileHandler} />
              <img src={uploadIcon} alt="upload" />
              <span style={{ color: forms.file ? "#000" : "#6c757d" }}>
                {forms.file ? forms.file.name : "Attach file..."}
              </span>
            </div>
          </div>

          {/* Budget Fix: Placeholder added */}
          <div className="col-lg-12 col-md-12 mt-20">
            <div className="xb-input-field">
              <input
                name="budget"
                type="text"
                value={forms.budget}
                onChange={changeHandler}
                placeholder="Your Budget (e.g. $500 - $1000)"
              />
              <img src={moneyIcon} alt="budget" />
            </div>
          </div>

          {/* Service */}
          <div className="col-lg-12 col-md-12 mt-20">
            <div className="xb-input-field xb-select-field">
              <select
                name="service"
                value={forms.service}
                onChange={changeHandler}
                required
                className="nice-select"
              >
                <option value="">Select Service*</option>
                <option value="AI - SaaS">AI-Powered SaaS Platforms</option>
                <option value="Building AI MVP">Building an AI MVP</option>
                <option value="AI chatbot virtual assistant">
                  AI Chatbots & Virtual Assistants
                </option>
                <option value="AI voice agents">AI Voice Agents</option>
                <option value="Genrative AI">
                  Generative AI & Content Systems
                </option>
                <option value="AI integration">
                  AI Integration into Existing Products
                </option>
              </select>
              <img src={listIcon} alt="list" />
            </div>
            {validator.message("service", forms.service, "required")}
          </div>

          {/* Message */}
          <div className="col-lg-12 col-md-12 mt-20">
            <div className="xb-input-field xb-massage-field">
              <textarea
                name="message"
                value={forms.message}
                onChange={changeHandler}
                placeholder="Your Message.."
                required
              ></textarea>
              <img src={messageIcon} alt="message" />
            </div>
            {validator.message("message", forms.message, "required")}
          </div>
        </div>

        <div className="form-submit-btn mt-35">
          <button
            type="submit"
            className="thm-btn form-btn"
            disabled={loading}
            style={{ opacity: loading ? 0.7 : 1 }}
          >
            {loading ? "Processing..." : "Submit Here"}
            <span className="xb-icon">
              <img src={arrowIcon} alt="arrow" />
            </span>
          </button>
        </div>
      </form>

      <style>{`
        .custom-status-popup {
          position: fixed;
          top: 30px;
          right: 30px;
          min-width: 250px;
          padding: 16px 24px;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          z-index: 999999 !important; /* Extremely high z-index for live sites */
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          animation: slideInRight 0.4s ease-out forwards;
        }
        .custom-status-popup.success { background-color: #00FF97; border-left: 5px solid #004d40; }
        .custom-status-popup.error { background-color: #ff5252; border-left: 5px solid #b71c1c; }

        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        /* Placeholder styling for Budget */
        .xb-input-field input::placeholder, 
        .xb-input-field textarea::placeholder {
          color: #6c757d;
          opacity: 1;
        }
      `}</style>
    </>
  );
};

export default ContactForm;
